import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { IamApi, iamErrorMessage, iamErrorReason, type IamUser, type OrganizationRef } from 'api/IamApi'
import { ApiError } from 'api/schema/core/ApiError'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Loader from 'components/Loader/Loader'
import PasswordRulesHint from 'components/PasswordRulesHint/PasswordRulesHint'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import UserAvatar from 'components/UserAvatar/UserAvatar'
import type { UserAudience } from 'hooks/user'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { assignableAudiences, canAssignDirectRobots, defaultAssignableAudience, generateTempPassword } from 'utils/iamAudiences'
import { newPasswordSchema } from 'utils/validationSchema'
import * as yup from 'yup'

import style from './IamUserEditor.module.css'

export type IamFacilityOption = { id: number; name: string }
export type IamRobotOption = { id: number; name: string }

const OBJECT_SUGGESTION_LIMIT = 80

const facilityAutocompleteSx = {
  width: '100%',
  maxWidth: 720,
  '& .MuiInputBase-root': {
    minHeight: 36,
    height: 'auto',
    backgroundColor: 'var(--glass-bg)',
    color: 'var(--text-main)',
    borderRadius: 'var(--radius-lg)',
    fontFamily: 'var(--font-main)',
    fontSize: 14,
    paddingTop: '4px',
    paddingBottom: '4px',
    '& fieldset': {
      border: 'var(--form-border) !important',
      borderRadius: 'var(--radius-lg)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--color-info) !important',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--color-info) !important',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--text-secondary) !important',
    },
  },
  '& .MuiAutocomplete-input': {
    color: 'var(--text-main) !important',
    fontSize: '14px',
    fontFamily: 'var(--font-main)',
    minWidth: '140px !important',
  },
  '& .MuiChip-root': {
    fontFamily: 'var(--font-main)',
    fontSize: 12,
    height: 24,
    background: 'rgba(158, 158, 158, 0.16)',
    color: 'var(--text-main)',
  },
  '& .MuiChip-deleteIcon': {
    color: 'var(--text-secondary) !important',
  },
}

const facilityPaperSx = {
  background: 'var(--glass-bg) !important',
  backdropFilter: 'var(--glass-blur) !important',
  WebkitBackdropFilter: 'var(--glass-blur) !important',
  borderRadius: 'var(--radius-md) !important',
  boxShadow: 'var(--glass-shadow) !important',
  border: 'var(--form-border) !important',
  color: 'var(--text-main) !important',
  '& .MuiAutocomplete-option': {
    fontFamily: 'var(--font-main)',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: 'var(--glass-bg-hover) !important',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'var(--color-info) !important',
      color: 'white !important',
    },
  },
}

type ObjectPickerProps = {
  options: IamFacilityOption[]
  selectedIds: Set<number>
  onChange: (ids: Set<number>) => void
  testId: string
  placeholder: string
  searchPlaceholder: string
  noMatches: string
  emptyHint: string
  selectedLabel: string
  clearLabel: string
}

const IamObjectPicker = ({
  options,
  selectedIds,
  onChange,
  testId,
  placeholder,
  searchPlaceholder,
  noMatches,
  emptyHint,
  selectedLabel,
  clearLabel,
}: ObjectPickerProps) => {
  const selected = options.filter(item => selectedIds.has(item.id))
  if (!options.length) {
    return <div className={style.hint}>{emptyHint}</div>
  }
  return (
    <>
      <div data-test-id={getTestId(testId)}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          filterSelectedOptions
          options={options}
          value={selected}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(next, { inputValue }) => {
            const query = inputValue.trim().toLowerCase()
            const matched = query
              ? next.filter(item => item.name.toLowerCase().includes(query))
              : next
            return matched.slice(0, OBJECT_SUGGESTION_LIMIT)
          }}
          onChange={(_, value) => onChange(new Set(value.map(item => item.id)))}
          limitTags={8}
          noOptionsText={noMatches}
          className={style.facilityPicker}
          slotProps={{ paper: { sx: facilityPaperSx } }}
          ListboxProps={{ style: { maxHeight: 280 } }}
          sx={facilityAutocompleteSx}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={selectedIds.size ? searchPlaceholder : placeholder}
            />
          )}
        />
      </div>
      {selectedIds.size ? (
        <div className={style.facilityMeta}>
          <span>{selectedLabel}</span>
          <button
            type="button"
            className={style.facilityClear}
            onClick={() => onChange(new Set())}
            data-test-id={getTestId(`${testId}-clear`)}
          >
            {clearLabel}
          </button>
        </div>
      ) : null}
    </>
  )
}

type Props = {
  isOpen: boolean
  mode: 'create' | 'edit'
  userId?: number
  actorAudience: UserAudience | null
  actorCanAssignInternalServiceman?: boolean
  facilities: IamFacilityOption[]
  robots: IamRobotOption[]
  onClose: () => void
  onSaved: (result: { user: IamUser; created: boolean; attached?: boolean; password?: string }) => void
}

const emailSchema = yup.string().email().required()

const IamUserEditor = ({
  isOpen,
  mode,
  userId,
  actorAudience,
  actorCanAssignInternalServiceman = false,
  facilities,
  robots,
  onClose,
  onSaved,
}: Props) => {
  const { t } = useTranslation()
  const [loadProgress, setLoadProgress] = useState(PROGRESS_TYPES.IDLE)
  const [saveProgress, setSaveProgress] = useState(PROGRESS_TYPES.IDLE)
  const [passwordProgress, setPasswordProgress] = useState(PROGRESS_TYPES.IDLE)
  const [user, setUser] = useState<IamUser | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [organizationId, setOrganizationId] = useState<number | null>(null)
  const [organizations, setOrganizations] = useState<OrganizationRef[]>([])
  const [audience, setAudience] = useState<UserAudience | ''>('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<Set<number>>(new Set())
  const [selectedRobotIds, setSelectedRobotIds] = useState<Set<number>>(new Set())
  const [formError, setFormError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [confirmDeactivate, setConfirmDeactivate] = useState(false)

  const organizationOptions: IOption[] = useMemo(
    () => [
      { value: '', text: t('account.users_organization_none') },
      ...organizations.map(org => ({ value: String(org.id), text: org.name })),
    ],
    [organizations, t]
  )
  const currentOrganizationOption =
    organizationOptions.find(option => option.value === (organizationId == null ? '' : String(organizationId))) ||
    organizationOptions[0]

  const assignable = useMemo(
    () =>
      assignableAudiences(actorAudience, {
        canAssignInternalServiceman: actorCanAssignInternalServiceman,
      }),
    [actorAudience, actorCanAssignInternalServiceman]
  )
  const identityEditable = mode === 'create' || Boolean(user?.can_edit_identity)
  const selectedOrganization = organizations.find(item => item.id === organizationId) ?? null
  const orgAllowsInternal = selectedOrganization?.can_assign_internal_serviceman === true
  const audienceOptions: IOption[] = useMemo(() => {
    const values = assignable.filter(value => value !== 'internal_serviceman' || orgAllowsInternal)
    if (user?.audience && !values.includes(user.audience)) {
      const hideInternal =
        identityEditable &&
        user.audience === 'internal_serviceman' &&
        (organizationId == null || selectedOrganization != null) &&
        !orgAllowsInternal
      if (!hideInternal) {
        values.unshift(user.audience)
      }
    }
    return values.map(value => ({ value, text: t(`account.audience_${value}`) }))
  }, [
    assignable,
    identityEditable,
    orgAllowsInternal,
    organizationId,
    selectedOrganization,
    t,
    user?.audience,
  ])
  const ownedFacilityIds = useMemo(() => new Set(facilities.map(item => item.id)), [facilities])
  const ownedRobotIds = useMemo(() => new Set(robots.map(item => item.id)), [robots])
  const assignEditable = mode === 'create' || Boolean(user?.can_assign_objects)
  const robotAssignEditable = assignEditable && canAssignDirectRobots(audience)
  const readOnly = !identityEditable && !assignEditable

  useEffect(() => {
    if (!identityEditable || audience !== 'internal_serviceman' || orgAllowsInternal) return
    if (organizationId != null && selectedOrganization == null) return
    const fallback = assignable.includes('serviceman')
      ? 'serviceman'
      : assignable.find(value => value !== 'internal_serviceman') || ''
    setAudience(fallback)
  }, [assignable, audience, identityEditable, orgAllowsInternal, organizationId, selectedOrganization])

  const resetCreate = useCallback(() => {
    setUser(null)
    setFirstName('')
    setLastName('')
    setEmail('')
    setOrganizationId(null)
    setOrganizations([])
    setAudience(
      defaultAssignableAudience(actorAudience, {
        canAssignInternalServiceman: actorCanAssignInternalServiceman,
      })
    )
    setPassword(generateTempPassword())
    setNewPassword('')
    setSelectedFacilityIds(new Set())
    setSelectedRobotIds(new Set())
    setFormError(null)
    setPasswordError(null)
    setPasswordSuccess(false)
    setLoadProgress(PROGRESS_TYPES.IDLE)
    setSaveProgress(PROGRESS_TYPES.IDLE)
  }, [actorAudience, actorCanAssignInternalServiceman])

  const applyUser = useCallback(
    (next: IamUser) => {
      setUser(next)
      setFirstName(next.first_name)
      setLastName(next.last_name)
      setEmail(next.email)
      setOrganizationId(next.organization?.id ?? null)
      setAudience(next.audience)
      setSelectedFacilityIds(new Set(next.facility_ids.filter(id => ownedFacilityIds.has(id))))
      setSelectedRobotIds(new Set(next.robot_ids.filter(id => ownedRobotIds.has(id))))
      setNewPassword('')
      setPasswordError(null)
      setPasswordSuccess(false)
      setFormError(null)
    },
    [ownedFacilityIds, ownedRobotIds]
  )

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    IamApi.listOrganizations()
      .then(items => {
        if (!cancelled) setOrganizations(items)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (mode === 'create') {
      resetCreate()
      return
    }
    if (!userId) return
    let cancelled = false
    setLoadProgress(PROGRESS_TYPES.WORK)
    IamApi.getUser(userId)
      .then(next => {
        if (cancelled) return
        applyUser(next)
        setLoadProgress(PROGRESS_TYPES.SUCCESS)
      })
      .catch(() => {
        if (cancelled) return
        setLoadProgress(PROGRESS_TYPES.ERROR)
        setFormError(t('account.users_load_error'))
      })
    return () => {
      cancelled = true
    }
  }, [applyUser, isOpen, mode, resetCreate, t, userId])

  const currentAudienceOption = audienceOptions.find(option => option.value === audience)

  const visibleRobotLabel = useMemo(() => {
    if (!user) return t('account.users_no_assigned_robots')
    if (user.visible_robot_ids == null) return t('account.users_robots_all')
    const names = user.visible_robot_ids.map(id => robots.find(item => item.id === id)?.name).filter(Boolean)
    if (!names.length) {
      return user.visible_robot_ids.length
        ? t('account.users_robots_count', { count: user.visible_robot_ids.length })
        : t('account.users_no_assigned_robots')
    }
    if (names.length <= 2) return names.join(', ')
    return t('account.users_robots_more', { first: names[0], count: names.length - 1 })
  }, [robots, t, user])

  const conflictMessage = (error: unknown): string => {
    const reason = iamErrorReason(error)
    if (reason === 'email_taken') return t('account.users_email_taken')
    if (reason === 'ask_higher_manager') return t('account.iam_reason_ask_higher_manager')
    if (reason === 'partial_scope') return t('account.iam_reason_partial_scope')
    if (reason === 'empty_acl') return t('account.iam_reason_empty_acl')
    if (reason === 'self') return t('account.iam_reason_self')
    return (
      iamErrorMessage(error, 'email') ||
      iamErrorMessage(error, 'audience') ||
      iamErrorMessage(error, 'facility_ids') ||
      iamErrorMessage(error, 'robot_ids') ||
      iamErrorMessage(error, 'password') ||
      iamErrorMessage(error) ||
      t('account.users_save_error')
    )
  }

  const onSave = async () => {
    setFormError(null)
    if (identityEditable) {
      if (!firstName.trim() || !lastName.trim()) {
        setFormError(t('account.users_name_required'))
        return
      }
      try {
        emailSchema.validateSync(email.trim())
      } catch {
        setFormError(t('signin.emailMessageValidError'))
        return
      }
      const audienceAllowed =
        Boolean(audience) &&
        (assignable.includes(audience as UserAudience) ||
          (mode === 'edit' && audience === user?.audience))
      if (!audienceAllowed) {
        setFormError(t('account.users_role_required'))
        return
      }
    }
    if (mode === 'create') {
      try {
        newPasswordSchema().validateSync(password)
      } catch (error) {
        setFormError(error instanceof yup.ValidationError ? t(String(error.message)) : t('account.users_save_error'))
        return
      }
    }

    setSaveProgress(PROGRESS_TYPES.WORK)
    try {
      if (mode === 'create') {
        const result = await IamApi.createUser({
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          audience: audience as UserAudience,
          password,
          organization_id: organizationId,
          facility_ids: Array.from(selectedFacilityIds),
          ...(canAssignDirectRobots(audience) ? { robot_ids: Array.from(selectedRobotIds) } : {}),
        })
        setSaveProgress(PROGRESS_TYPES.SUCCESS)
        onSaved({
          user: result.user,
          created: result.created,
          attached: !result.created,
          password: result.created ? password : undefined,
        })
        return
      }
      if (!user) return
      const payload: Parameters<typeof IamApi.updateUser>[1] = {}
      if (identityEditable) {
        if (firstName.trim() !== user.first_name) payload.first_name = firstName.trim()
        if (lastName.trim() !== user.last_name) payload.last_name = lastName.trim()
        if (email.trim() !== user.email) payload.email = email.trim()
        const nextOrganizationId = user.organization?.id ?? null
        if (organizationId !== nextOrganizationId) payload.organization_id = organizationId
        if (audience && audience !== user.audience) payload.audience = audience as UserAudience
      }
      if (assignEditable) {
        const currentOwned = user.facility_ids.filter(id => ownedFacilityIds.has(id)).sort((a, b) => a - b)
        const nextOwned = Array.from(selectedFacilityIds).sort((a, b) => a - b)
        if (currentOwned.join(',') !== nextOwned.join(',')) {
          payload.facility_ids = nextOwned
        }
        if (canAssignDirectRobots(audience)) {
          const currentRobots = user.robot_ids.filter(id => ownedRobotIds.has(id)).sort((a, b) => a - b)
          const nextRobots = Array.from(selectedRobotIds).sort((a, b) => a - b)
          if (currentRobots.join(',') !== nextRobots.join(',')) {
            payload.robot_ids = nextRobots
          }
        }
      }
      if (!Object.keys(payload).length) {
        setSaveProgress(PROGRESS_TYPES.IDLE)
        onClose()
        return
      }
      const updated = await IamApi.updateUser(user.id, payload)
      setSaveProgress(PROGRESS_TYPES.SUCCESS)
      onSaved({ user: updated, created: false })
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setFormError(t('account.users_throttled'))
      } else {
        setFormError(conflictMessage(error))
      }
      setSaveProgress(PROGRESS_TYPES.ERROR)
    }
  }

  const onSetPassword = async () => {
    if (!user) return
    setPasswordError(null)
    setPasswordSuccess(false)
    try {
      newPasswordSchema().validateSync(newPassword)
    } catch (error) {
      setPasswordError(error instanceof yup.ValidationError ? t(String(error.message)) : t('account.users_save_error'))
      return
    }
    setPasswordProgress(PROGRESS_TYPES.WORK)
    try {
      const updated = await IamApi.setPassword(user.id, newPassword)
      setUser(updated)
      setNewPassword('')
      setPasswordProgress(PROGRESS_TYPES.SUCCESS)
      setPasswordSuccess(true)
    } catch (error) {
      setPasswordProgress(PROGRESS_TYPES.ERROR)
      setPasswordError(
        error instanceof ApiError && error.status === 429
          ? t('account.users_throttled')
          : iamErrorMessage(error, 'password') || conflictMessage(error)
      )
    }
  }

  const onToggleActive = async () => {
    if (!user) return
    setFormError(null)
    setSaveProgress(PROGRESS_TYPES.WORK)
    try {
      const updated = user.is_active ? await IamApi.deactivateUser(user.id) : await IamApi.reactivateUser(user.id)
      setSaveProgress(PROGRESS_TYPES.SUCCESS)
      setConfirmDeactivate(false)
      onSaved({ user: updated, created: false })
    } catch (error) {
      setSaveProgress(PROGRESS_TYPES.ERROR)
      setConfirmDeactivate(false)
      setFormError(
        error instanceof ApiError && error.status === 429 ? t('account.users_throttled') : conflictMessage(error)
      )
    }
  }

  const reasonKey = user?.cannot_edit_reason ? `account.iam_reason_${user.cannot_edit_reason}` : ''
  const editorTitle =
    mode === 'create' ? t('account.users_create_title') : [firstName, lastName].filter(Boolean).join(' ') || email || t('account.users_edit_title')

  if (!isOpen) return null

  return (
    <>
      <div className={style.panel} data-test-id={getTestId('iam-editor')}>
        <div className={style.headerArea}>
          <button type="button" className={style.backLink} onClick={onClose} data-test-id={getTestId('iam-back')}>
            ← {t('account.users_back_to_list')}
          </button>
          <div className={style.titleRow}>
            {mode === 'edit' ? (
              <UserAvatar
                url={user?.avatar_url}
                firstName={firstName}
                lastName={lastName}
                email={email}
                size={72}
              />
            ) : null}
            <div className={style.title} data-test-id={getTestId('iam-editor-title')}>
              {editorTitle}
            </div>
          </div>
          {mode === 'create' ? <div className={style.subtitle}>{t('account.users_create_subtitle')}</div> : null}
          {mode === 'edit' && email ? <div className={style.meta}>{email}</div> : null}
        </div>

        {loadProgress === PROGRESS_TYPES.WORK ? <Loader /> : null}
        {loadProgress === PROGRESS_TYPES.ERROR ? <div className={style.errorText}>{formError}</div> : null}
        {(mode === 'create' || loadProgress === PROGRESS_TYPES.SUCCESS) && (
          <form
            className={style.content}
            onSubmit={event => {
              event.preventDefault()
              void onSave()
            }}
          >
            {reasonKey ? (
              <div className={style.banner} data-test-id={getTestId('iam-readonly-hint')}>
                {t(reasonKey)}
              </div>
            ) : null}

            <div className={style.section}>
              <div className={style.sectionTitle}>{t('account.users_section_details')}</div>
              <div className={style.fieldsGrid}>
                <TextFieldDescribed
                  description={t('account.users_first_name')}
                  placeholder={t('account.users_first_name')}
                  value={firstName}
                  onChange={event => setFirstName(event.target.value)}
                  disabled={!identityEditable}
                  dataTestId={getTestId('iam-first-name')}
                  labelClassName={style.formLabel}
                />
                <TextFieldDescribed
                  description={t('account.users_last_name')}
                  placeholder={t('account.users_last_name')}
                  value={lastName}
                  onChange={event => setLastName(event.target.value)}
                  disabled={!identityEditable}
                  dataTestId={getTestId('iam-last-name')}
                  labelClassName={style.formLabel}
                />
                <TextFieldDescribed
                  description={t('account.email_label')}
                  placeholder={t('account.email_label')}
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  disabled={!identityEditable}
                  dataTestId={getTestId('iam-email')}
                  labelClassName={style.formLabel}
                />
                <Dropdown
                  options={organizationOptions}
                  currentOption={currentOrganizationOption}
                  placeholder={t('account.users_organization')}
                  description={t('account.users_organization')}
                  descriptionClassName={style.formLabel}
                  disabled={!identityEditable || !organizationOptions.length}
                  onClickOption={option =>
                    setOrganizationId(option.value === '' ? null : Number(option.value))
                  }
                  dataTestId="iam-organization"
                />
                <Dropdown
                  options={audienceOptions}
                  currentOption={currentAudienceOption}
                  placeholder={t('account.users_role')}
                  description={t('account.users_role')}
                  descriptionClassName={style.formLabel}
                  disabled={!identityEditable || !audienceOptions.length}
                  onClickOption={option => setAudience(String(option.value) as UserAudience)}
                  dataTestId="iam-audience"
                />
              </div>
            </div>

            {mode === 'create' ? (
              <div className={style.section}>
                <div className={style.sectionTitle}>{t('account.users_temp_password')}</div>
                <div className={style.passwordBlock}>
                  <div className={style.passwordRow}>
                    <TextFieldDescribed
                      description={t('account.users_temp_password')}
                      placeholder={t('account.users_temp_password')}
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      dataTestId={getTestId('iam-password')}
                      labelClassName={style.formLabel}
                      className={style.passwordField}
                    />
                    <Button.Outlined
                      onClick={() => setPassword(generateTempPassword())}
                      dataTestId="iam-generate-password"
                      className={style.passwordAction}
                    >
                      {t('account.users_generate_password')}
                    </Button.Outlined>
                  </div>
                  <PasswordRulesHint />
                </div>
              </div>
            ) : null}

            <div className={style.section}>
              <div className={style.sectionTitle}>{t('account.users_facilities')}</div>
              {assignEditable ? (
                <>
                  <div className={style.hint}>{t('account.users_facilities_hint')}</div>
                  <IamObjectPicker
                    options={facilities}
                    selectedIds={selectedFacilityIds}
                    onChange={setSelectedFacilityIds}
                    testId="iam-facilities"
                    placeholder={t('account.users_facilities_placeholder')}
                    searchPlaceholder={t('account.users_facilities_search')}
                    noMatches={t('account.users_no_facility_matches')}
                    emptyHint={t('account.users_no_facilities')}
                    selectedLabel={t('account.users_facilities_selected', { count: selectedFacilityIds.size })}
                    clearLabel={t('account.users_facilities_clear')}
                  />
                </>
              ) : (
                <div className={style.hint}>{t('account.users_facilities_readonly')}</div>
              )}
            </div>

            <div className={style.section}>
              <div className={style.sectionTitle}>{t('account.users_robots')}</div>
              {robotAssignEditable ? (
                <>
                  <div className={style.hint}>{t('account.users_robots_hint')}</div>
                  <IamObjectPicker
                    options={robots}
                    selectedIds={selectedRobotIds}
                    onChange={setSelectedRobotIds}
                    testId="iam-robots"
                    placeholder={t('account.users_robots_placeholder')}
                    searchPlaceholder={t('account.users_robots_search')}
                    noMatches={t('account.users_no_robot_matches')}
                    emptyHint={t('account.users_no_robots')}
                    selectedLabel={t('account.users_robots_selected', { count: selectedRobotIds.size })}
                    clearLabel={t('account.users_robots_clear')}
                  />
                </>
              ) : (
                <>
                  <div className={style.hint}>
                    {canAssignDirectRobots(audience)
                      ? t('account.users_robots_readonly')
                      : t('account.users_robots_via_facilities')}
                  </div>
                  {mode === 'edit' ? (
                    <div className={style.hint} data-test-id={getTestId('iam-robots-visible')}>
                      {visibleRobotLabel}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {mode === 'edit' && identityEditable ? (
              <div className={style.section}>
                <div className={style.sectionTitle}>{t('account.users_set_password')}</div>
                <div className={style.passwordBlock}>
                  <div className={style.passwordRow}>
                    <TextFieldDescribed
                      description={t('account.new_password')}
                      placeholder={t('account.new_password')}
                      value={newPassword}
                      onChange={event => {
                        setNewPassword(event.target.value)
                        setPasswordSuccess(false)
                      }}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          void onSetPassword()
                        }
                      }}
                      dataTestId={getTestId('iam-set-password')}
                      labelClassName={style.formLabel}
                      className={style.passwordField}
                      hasError={Boolean(passwordError)}
                      error={passwordError || ''}
                    />
                    <Button.Outlined
                      onClick={() => {
                        setNewPassword(generateTempPassword())
                        setPasswordError(null)
                        setPasswordSuccess(false)
                      }}
                      dataTestId="iam-set-password-generate"
                      className={style.passwordAction}
                    >
                      {t('account.users_generate_password')}
                    </Button.Outlined>
                    <Button.Outlined
                      onClick={onSetPassword}
                      loading={passwordProgress === PROGRESS_TYPES.WORK}
                      disabled={passwordProgress === PROGRESS_TYPES.WORK}
                      dataTestId="iam-set-password-save"
                      className={style.passwordAction}
                    >
                      {t('account.save_password')}
                    </Button.Outlined>
                  </div>
                  <PasswordRulesHint />
                </div>
                {passwordSuccess ? <div className={style.successText}>{t('account.password_changed')}</div> : null}
              </div>
            ) : null}

            {formError && loadProgress !== PROGRESS_TYPES.ERROR ? (
              <div className={style.errorText} data-test-id={getTestId('iam-form-error')}>
                {formError}
              </div>
            ) : null}

            <div className={style.actions}>
              {mode === 'edit' && identityEditable && user?.is_active ? (
                <Button.Outlined onClick={() => setConfirmDeactivate(true)} dataTestId="iam-toggle-active">
                  {t('account.users_deactivate')}
                </Button.Outlined>
              ) : null}
              {mode === 'edit' && identityEditable && user && !user.is_active && actorAudience === 'developer' ? (
                <Button.Outlined onClick={() => void onToggleActive()} dataTestId="iam-toggle-active">
                  {t('account.users_reactivate')}
                </Button.Outlined>
              ) : null}
              <div className={style.actionsRight}>
                {!readOnly ? (
                  <Button.Standart
                    type="submit"
                    loading={saveProgress === PROGRESS_TYPES.WORK}
                    disabled={saveProgress === PROGRESS_TYPES.WORK}
                    dataTestId="iam-save"
                  >
                    {t('common.save')}
                  </Button.Standart>
                ) : null}
              </div>
            </div>
          </form>
        )}
      </div>
      <ConfirmDialog
        isOpen={confirmDeactivate}
        onClose={() => setConfirmDeactivate(false)}
        onConfirmClick={onToggleActive}
        title={t('account.users_deactivate_title')}
        confirmText={t('account.users_deactivate')}
        danger
      >
        {t('account.users_deactivate_confirm')}
      </ConfirmDialog>
    </>
  )
}

export default IamUserEditor
